import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import Logo from '../Logo/Logo';
import { newArrivals, featuredCollection, specialOne, highlights } from '../../data/products';
import { WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from '../../data/siteInfo';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Popular search suggestions
  const popularSuggestions = [
    'Birthday Decor',
    'Anniversary Decor',
    'Baby Shower',
    'Wedding Decor',
    'Proposal Decor',
    'Balloon Arch',
    'Car Boot Decor',
    'Golden Decor',
    'Pink Decor',
    'Romantic Decor'
  ];

  // Get all product titles for suggestions
  const allProducts = useMemo(() => {
    return [
      ...newArrivals,
      ...featuredCollection,
      ...specialOne,
      ...highlights.birthday,
      ...highlights.anniversary,
      ...highlights.babyShower,
      ...highlights.gifting
    ];
  }, []);

  // Filter suggestions based on search query
  const getSuggestions = () => {
    if (!searchQuery.trim()) {
      return popularSuggestions.slice(0, 6);
    }

    const query = searchQuery.toLowerCase().trim();
    const suggestions = [];

    // Add matching product titles
    allProducts.forEach((product) => {
      if (product.title.toLowerCase().includes(query)) {
        if (!suggestions.includes(product.title) && suggestions.length < 5) {
          suggestions.push(product.title);
        }
      }
    });

    // Add matching popular suggestions
    popularSuggestions.forEach((suggestion) => {
      if (suggestion.toLowerCase().includes(query)) {
        if (!suggestions.includes(suggestion) && suggestions.length < 6) {
          suggestions.push(suggestion);
        }
      }
    });

    return suggestions.slice(0, 6);
  };

  const suggestions = getSuggestions();

  const handleSearch = (e, query = null) => {
    e?.preventDefault();
    const searchTerm = query || searchQuery.trim();
    if (searchTerm) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    handleSearch(null, suggestion);
  };

  const handleSearchClick = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSearchOpen && !event.target.closest('.search-container')) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top Banner */}
      <div className="bg-[#8B1538] text-white text-center py-2 px-4">
        <p className="text-xs sm:text-sm">
          Book Your Decor – Contact Us{' '}
          <a href={`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`} className="hover:underline">
            {WHATSAPP_DISPLAY}
          </a>
        </p>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Left: Logo + Desktop Navigation (lg only) */}
            <div className="flex items-center min-w-0 gap-4 lg:gap-8">
              <div className="flex-shrink-0">
                <a href="/" className="flex items-center" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                  <Logo />
                </a>
              </div>
              <div className="hidden lg:flex items-center">
                <Navigation />
              </div>
            </div>

            {/* Icons - Desktop: Search + Account + Cart */}
            <div className="hidden lg:flex items-center space-x-4 flex-shrink-0">
              {/* Search - Desktop */}
              <div className="relative search-container">
                <button
                  onClick={handleSearchClick}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>
                {/* Search Dropdown */}
                {isSearchOpen && (
                  <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                    <form onSubmit={handleSearch} className="p-4 border-b border-gray-200">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search products..."
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B1538] focus:border-transparent"
                          autoFocus
                        />
                        <button
                          type="submit"
                          className="bg-[#8B1538] text-white px-4 py-2 rounded-lg hover:bg-[#6B0F2A] transition-colors whitespace-nowrap"
                        >
                          Search
                        </button>
                      </div>
                    </form>
                    {/* Search Suggestions */}
                    {suggestions.length > 0 && (
                      <div className="p-2 max-h-64 overflow-y-auto">
                        <p className="text-xs text-gray-500 px-3 py-2 font-medium">
                          {searchQuery.trim() ? 'Suggestions' : 'Popular Searches'}
                        </p>
                        <div className="space-y-1">
                          {suggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => handleSuggestionClick(suggestion)}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm text-gray-700 flex items-center gap-2"
                            >
                              <svg
                                className="w-4 h-4 text-gray-400 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                              <span className="truncate">{suggestion}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Account Icon */}
              <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </button>
              {/* Cart Icon */}
              <button className="p-2 hover:bg-gray-100 rounded-full transition-colors relative">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span className="absolute top-0 right-0 bg-[#8B1538] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  0
                </span>
              </button>
            </div>

            {/* Mobile: Search + Menu in one container */}
            <div className="lg:hidden flex items-center space-x-2">
              {/* Search */}
              <div className="relative search-container">
                <button
                  onClick={handleSearchClick}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>
                {/* Search Dropdown */}
                {isSearchOpen && (
                  <div className="fixed left-0 right-0 top-[5rem] sm:absolute sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-w-none bg-white rounded-lg sm:rounded-lg shadow-xl border border-gray-200 z-50 sm:z-50">
                    <form onSubmit={handleSearch} className="p-4 border-b border-gray-200">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search products..."
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B1538] focus:border-transparent"
                          autoFocus
                        />
                        <button
                          type="submit"
                          className="bg-[#8B1538] text-white px-4 py-2 rounded-lg hover:bg-[#6B0F2A] transition-colors whitespace-nowrap"
                        >
                          Search
                        </button>
                      </div>
                    </form>
                    {/* Search Suggestions */}
                    {suggestions.length > 0 && (
                      <div className="p-2 max-h-64 overflow-y-auto">
                        <p className="text-xs text-gray-500 px-3 py-2 font-medium">
                          {searchQuery.trim() ? 'Suggestions' : 'Popular Searches'}
                        </p>
                        <div className="space-y-1">
                          {suggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => handleSuggestionClick(suggestion)}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm text-gray-700 flex items-center gap-2"
                            >
                              <svg
                                className="w-4 h-4 text-gray-400 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                              <span className="truncate">{suggestion}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Mobile Menu Button */}
              <button
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {/* Backdrop */}
        {isMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
            onClick={() => setIsMenuOpen(false)}
          />
        )}
        {/* Drawer */}
        <div
          className={`lg:hidden fixed top-0 right-0 h-full w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <Logo className="h-9" />
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <Navigation />
            </div>
            {/* Drawer Footer - Built By */}
            <div className="border-t border-gray-200 p-4 mt-auto">
              <a
                href={`https://wa.me/917988537088?text=${encodeURIComponent('welcome to ssd tech')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-gray-600 hover:text-[#8B1538] transition-colors group"
              >
                <span className="text-sm font-medium">Built By : SSD-TECH-PROG_</span>
                <div className="w-8 h-8 bg-[#F44336] rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                  <svg
                    className="w-5 h-5 text-[#FFC107]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </div>
              </a>
            </div>
          </div>
        </div>
      </nav>
      {/* Click outside to close search */}
      {isSearchOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsSearchOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;

